import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  styled,
  Typography,
  IconButton,
  Drawer,
  Link,
  useTheme,
  useMediaQuery,
  Collapse,
  Chip,
  Tooltip
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useTabContextSafe } from '../../contexts/TabContext';
import { ROUTE_COMPONENT_MAP, getTransientRouteTitle } from './routeComponentMap';
import { isFormDirty } from '../../utils/formLeaveGuard';
import FormLeaveConfirmDialog from './FormLeaveConfirmDialog';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import SidebarSettingsModal from './SidebarSettingsModal';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../config/brand';
import { APP_VERSION } from '../../version';
import { mainApi } from '../../config/apiClient';
import {
  isSectionAllowedForSubAdmin,
  isItemAllowedForSubAdmin,
} from '../users/adminModuleMap';

// İkonlar
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MessageIcon from '@mui/icons-material/Message';
import PollIcon from '@mui/icons-material/Poll';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import BusinessIcon from '@mui/icons-material/Business';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import EventIcon from '@mui/icons-material/Event';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SendIcon from '@mui/icons-material/Send';
import GetAppIcon from '@mui/icons-material/GetApp';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DescriptionIcon from '@mui/icons-material/Description';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ArticleIcon from '@mui/icons-material/Article';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import TvIcon from '@mui/icons-material/Tv';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import CollectionsIcon from '@mui/icons-material/Collections';
import DevicesIcon from '@mui/icons-material/Devices';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LocalShippingIcon from '@mui/icons-material/LocalShipping'; // ✅ Paket Yönetimi ikonu
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'; // ✅ Çek/Senet ikonu
import AccountTreeIcon from '@mui/icons-material/AccountTree'; // ✅ Projeler (PYS) ikonu
import MenuBookIcon from '@mui/icons-material/MenuBook'; // ✅ Çek Defteri ikonu
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BuildIcon from '@mui/icons-material/Build';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BarChartIcon from '@mui/icons-material/BarChart';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import { getAccessibleReports } from '../reports/hub/reportsHubCatalog';
// Local storage anahtarı ve varsayılan renk (BRAND'den alınır)
const SIDEBAR_COLOR_KEY = 'gts_sidebar_color';
const DEFAULT_SIDEBAR_COLOR = BRAND.colors.sidebarDefault;
const SIDEBAR_VISIBLE_BUTTONS_KEY = 'gts_sidebar_visible_buttons';
// Yeni anahtarlar
const SIDEBAR_VISIBLE_HEADERS_KEY = 'gts_sidebar_visible_headers';
const SIDEBAR_VISIBLE_ITEMS_KEY = 'gts_sidebar_visible_items';
// Menü açık/kapalı durumu için localStorage anahtarları
const TASK_TRACKING_OPEN_KEY = 'gts_task_tracking_open';
const ACCOUNTING_OPEN_KEY = 'gts_accounting_open';
const INVOICE_OPEN_KEY = 'gts_invoice_open';
const OFFER_OPEN_KEY = 'gts_offer_open';
const SALES_SYSTEM_OPEN_KEY = 'gts_sales_system_open';
const HR_OPEN_KEY = 'gts_hr_open';
const WMS_OPEN_KEY = 'gts_wms_open';
const CARD_MANAGEMENT_OPEN_KEY = 'gts_card_management_open';
const BRANCHES_OPEN_KEY = 'gts_branches_open';
const OTHER_OPEN_KEY = 'gts_other_open';

// Sidebar butonlarının listesi
const SIDEBAR_BUTTONS = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
  { id: 'tasks', label: 'Görevler', path: '/tasks' },
  { id: 'personnel', label: 'İnsan Kaynakları', path: '/personnel' },
  { id: 'shifts', label: 'Vardiyalar', path: '/shifts' },
  { id: 'reports', label: 'Raporlar', path: '/reports' },
  { id: 'messages', label: 'Mesajlar', path: '/messages' },
  { id: 'surveys', label: 'Anketler', path: '/surveys' },
  { id: 'events', label: 'Etkinlikler', path: '/events' },
  { id: 'reservations', label: 'Rezervasyonlar', path: '/reservations' },
  { id: 'branches', label: 'Şubeler', path: '/branches' }
];

// Görev Takip altında bulunan butonlar
const TASK_TRACKING_BUTTONS = [
  { id: 'dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { id: 'tasks', path: '/tasks', icon: <AssignmentIcon /> },
  { id: 'reports', path: '/reports', icon: <AssessmentIcon /> },
  { id: 'messages', path: '/messages', icon: <MessageIcon /> },
  { id: 'surveys', path: '/surveys', icon: <PollIcon /> },
];

// Ön Muhasebe altındaki normal butonlar
const ACCOUNTING_BUTTONS = [
  { id: 'dashboard', path: '/accounting/dashboard', icon: <DashboardIcon /> },
  { id: 'current', path: '/cari', icon: <PersonIcon /> },
  { id: 'stock', path: '/stok', icon: <InventoryIcon /> },
  // Etiket yazdırma sidebar'dan kaldırıldı; Stok ekranındaki üç nokta
  // menüsünden açılıyor (bkz. FeaturesMenu → labelPrinting).
  { id: 'bank', path: '/banka', icon: <AccountBalanceIcon /> },
  { id: 'cash', path: '/kasa', icon: <LocalAtmIcon /> },
  { id: 'warehouse', path: '/depo', icon: <WarehouseIcon /> },
  { id: 'currency', path: '/doviz', icon: <CurrencyExchangeIcon /> },
  { id: 'branchPrices', path: '/sube-fiyatlari', icon: <LocalOfferIcon /> }, // ✅ YENİ: Şube Fiyatları
  { id: 'imageLibrary', path: '/resim-kutuphanesi', icon: <CollectionsIcon /> },
];

// Teklifler alt menü butonları
const OFFER_SUBMENU = [
  // Boş array, artık alt menü yok
];

// Fatura alt menü butonları
const INVOICE_SUBMENU = [
  // Boş array, artık alt menü yok
];

// İnsan Kaynakları altındaki butonlar
const HR_BUTTONS: { id: string; path: string; icon: React.ReactNode }[] = [
  { id: 'personnel', path: '/personnel', icon: <PeopleIcon /> },
  { id: 'shifts', path: '/shifts', icon: <AccessTimeIcon /> },
  { id: 'pdks', path: '/hr/pdks', icon: <FingerprintIcon /> },
  { id: 'leaveManagement', path: '/ik/izin', icon: <EventAvailableIcon /> },
  { id: 'performanceManagement', path: '/ik/performans', icon: <TrendingUpIcon /> },
  { id: 'trainingDevelopment', path: '/ik/egitim', icon: <SchoolIcon /> },
  { id: 'payroll', path: '/ik/bordro', icon: <RequestQuoteIcon /> },
  { id: 'documentManagement', path: '/ik/dokuman', icon: <ArticleIcon /> },
  { id: 'recruitment', path: '/ik/isealim', icon: <PersonAddIcon /> },
  { id: 'cvApplications', path: '/hr/cv-application', icon: <DescriptionIcon /> },
  // { id: 'ik-dashboard', path: '/ik/dashboard', icon: <DashboardIcon /> },
];

const TEMP_HIDDEN_HR_BUTTON_IDS = new Set([
  'leaveManagement',
  'performanceManagement',
  'trainingDevelopment',
  'payroll',
  'documentManagement',
]);

// Şubeler altındaki butonlar
const BRANCHES_BUTTONS = [
  { id: 'branches', path: '/branches', icon: <BusinessIcon /> },
];

// Diğer butonlar
const OTHER_BUTTONS = [
  { id: 'events', path: '/events', icon: <EventIcon /> },
  { id: 'reservations', path: '/reservations', icon: <EventSeatIcon /> },
  { id: 'mailReports', path: '/accounting/mail-raporlari', icon: <MarkEmailReadIcon /> },
  // Çoklu admin sistemi: yalnız ana admin (role==='admin') görür — render'da filtrelenir.
  { id: 'users', path: '/kullanicilar', icon: <PeopleIcon /> },
];

// Geçici gizlenen "Diğer" butonları. Yalnızca 'events' (Etkinlikler) henüz
// SQL'e taşınmadığı için sidebar'da gizli. Taşınınca buradan çıkarılacak. ('reservations' SQL'e taşındı — açıldı.)
const TEMP_HIDDEN_OTHER_BUTTON_IDS = new Set([
  'events',
]);

// Satış Sistemi altındaki butonlar
// Satış Sistemi altındaki butonlar (Güncellenmiş)
const SALES_SYSTEM_BUTTONS = [
 { id: 'kitchenScreen', path: '/mutfak-ekrani-secimi', icon: <RestaurantIcon /> }, 
 { id: 'fastSale', path: '/hizli-satis', icon: <FlashOnIcon /> },
 { id: 'restaurant', path: '/restoran', icon: <RestaurantIcon /> }, 
 { id: 'orderManagement', path: '/siparis-yonetimi', icon: <LocalShippingIcon /> }, // ✅ Paket Yönetim Sistemi
 { id: 'qrMenu', path: '/qr-menu-management', icon: <QrCode2Icon /> },
  // { id: 'poleDisplay', path: '/musteri-ekrani-yonetimi', icon: <TvIcon />, label: 'Müşteri Ekranı' }, // ✅ Flutter uygulamasına taşındı
 { id: 'kioskMenu', path: '/kiosk-menu', icon: <TouchAppIcon /> },
 { id: 'eCommerce', path: '/e-commerce', icon: <ShoppingBasketIcon /> }, 
 { id: 'courier', path: '/kurye', icon: <DeliveryDiningIcon /> }, 
 { id: 'salesReports', path: '/gun-sonu', icon: <AssessmentIcon /> }, // ✅ YENİ: Satış Raporları (Gün Sonu)
 { id: 'integrations', path: '/entegrasyonlar', icon: <IntegrationInstructionsIcon /> },
 { id: 'haciogluMapping', path: '/hacioglu-urun-eslestirme', icon: <CompareArrowsIcon />, requireHacioglu: true },
 // clubEntrance kaldırıldı — kulüp girişi artık hızlı satış ekranı ayarlarındaki
 // "Kulüp Girişi" sekmesiyle (mod olarak) yönetiliyor; ayrı menü butonu gereksiz.
 { id: 'imageLibrary', path: '/resim-kutuphanesi', icon: <CollectionsIcon /> },
];

// Akıllı Depo WMS altındaki butonlar
const WMS_BUTTONS: { id: string; path: string; icon: React.ReactNode }[] = [
  { id: 'wmsPanel', path: '/wms/panel', icon: <DashboardIcon /> },
  { id: 'wmsWarehouseSetup', path: '/wms/depo-tanimlama', icon: <WarehouseIcon /> },
  { id: 'wmsOrderPool', path: '/wms/siparis-havuzu', icon: <ShoppingCartIcon /> },
  { id: 'wmsWaves', path: '/wms/is-emirleri', icon: <AssignmentIcon /> },
  { id: 'wmsPickingSetup', path: '/wms/toplama-ayarlari', icon: <PlaylistAddCheckIcon /> },
  { id: 'wmsCancelMgmt', path: '/wms/iptal-yonetimi', icon: <CancelOutlinedIcon /> },
  { id: 'wmsPackingSetup', path: '/wms/paketleme-ayarlari', icon: <Inventory2Icon /> },
  { id: 'wmsReceivingSetup', path: '/wms/mal-kabul-ayarlari', icon: <MoveToInboxIcon /> },
  { id: 'wmsCycleCountSetup', path: '/wms/stok-sayim-ayarlari', icon: <FactCheckIcon /> },
  { id: 'wmsReceivingMgmt', path: '/wms/mal-kabul-yonetimi', icon: <LocalShippingIcon /> },
  { id: 'wmsCycleCountMgmt', path: '/wms/stok-sayim-yonetimi', icon: <InventoryIcon /> },
  { id: 'wmsEquipment', path: '/wms/ekipman-yonetimi', icon: <BuildIcon /> },
  { id: 'wmsReports', path: '/wms/raporlar', icon: <BarChartIcon /> },
];

// Cihaz Yönetimi altındaki butonlar
const DEVICE_MANAGEMENT_BUTTONS = [
  { id: 'deviceManagement', path: '/cihaz-yonetimi', icon: <DevicesIcon /> },
  { id: 'scales', path: '/tartilar', icon: <MonitorWeightIcon /> },
];

const CARD_MANAGEMENT_BUTTONS = [
  { id: 'cards', path: '/kart-yonetimi/kartlar', icon: <CreditCardIcon /> },
  { id: 'accessManagement', path: '/kart-yonetimi/gecis-yonetimi', icon: <MeetingRoomIcon /> },
];

// Hızlı Başlangıç flyout menü butonları
const QUICK_START_BUTTONS = [
  { id: 'reports', title: 'Raporlar', icon: <AssessmentIcon />, color: '#673ab7', path: '/reports' },
  { id: 'task_tracking', titleKey: 'sidebar.taskTracking', icon: <TrackChangesIcon />, color: BRAND.colors.secondary, storageKey: 'gts_task_tracking_open' },
  { id: 'hr', titleKey: 'sidebar.humanResources', icon: <SupervisedUserCircleIcon />, color: '#9c27b0', storageKey: 'gts_hr_open' },
  { id: 'accounting', titleKey: 'sidebar.preAccounting', icon: <AccountBalanceWalletIcon />, color: '#f57c00', storageKey: 'gts_accounting_open' },
  { id: 'sales_system', titleKey: 'sidebar.salesSystem', icon: <StorefrontIcon />, color: '#388e3c', storageKey: 'gts_sales_system_open' },
  { id: 'device_management', titleKey: 'sidebar.deviceManagement', icon: <DevicesIcon />, color: '#0097a7', storageKey: 'gts_device_management_open' },
  { id: 'card_management', titleKey: 'sidebar.cardManagement', icon: <CreditCardIcon />, color: '#7b1fa2', storageKey: CARD_MANAGEMENT_OPEN_KEY },
  { id: 'branches', titleKey: 'sidebar.branches', icon: <BusinessIcon />, color: '#d32f2f', storageKey: 'gts_branches_open' },
];

const SidebarContainer = styled(Box)<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  width: isCollapsed ? 70 : 260,
  height: '100vh',
  backgroundColor: 'var(--sidebar-color, #102648)',
  color: 'var(--sidebar-text-color, white)',
  display: 'flex',
  flexDirection: 'column',
  position: 'fixed',
  left: 0,
  top: 0,
  zIndex: 1000, // Toggle butonundan bir alt seviye
  boxShadow: '0 0 10px rgba(0,0,0,0.3)',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: 'visible', // Toggle butonunun görünmesi için overflow'u visible yap
  '@media (max-width: 1399px)': {
    display: 'none', // lg (1200px) altında MobileMenu kullanılacak
  },
}));

const Logo = styled(Box)<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  backgroundColor: 'var(--sidebar-color, #102648)',
  color: 'var(--sidebar-text-color, white)',
  '@media (max-width: 1399px)': {
    padding: theme.spacing(1.5),
  },
}));

const LogoImage = styled('img')<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  width: isCollapsed ? 40 : 70,

  height: isCollapsed ? 40 : 70,
  
  borderRadius: 10,
  marginBottom: isCollapsed ? 0 : 10,
  transition: 'all 0.3s ease',
  '@media (max-width: 1399px)': {
    width: isCollapsed ? 30 : 50,
    height: isCollapsed ? 30 : 50,
    marginBottom: isCollapsed ? 0 : 5,
  },
}));

const MenuContainer = styled(List)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2, 0),
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.05)',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(255, 255, 255, 0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05)',
  '@media (max-width: 1399px)': {
    padding: theme.spacing(0.3, 0),
  },
}));

const MenuItem = styled(ListItem)<{ active?: boolean; isCollapsed: boolean }>(({ theme, active, isCollapsed }) => ({
  padding: theme.spacing(1.5, isCollapsed ? 2 : 3),
  marginBottom: theme.spacing(0.5),
  cursor: 'pointer',
  borderLeft: active ? '4px solid var(--sidebar-text-color, white)' : '4px solid transparent',
  backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
  color: 'var(--sidebar-text-color, white)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  '@media (max-width: 1399px)': {
    padding: theme.spacing(0.4, isCollapsed ? 0.5 : 1),
    marginBottom: 1,
    minHeight: 28,
  },
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  borderTop: '1px solid rgba(255,255,255,0.1)',
  fontSize: '0.75rem',
  opacity: 0.8,
  color: 'var(--sidebar-text-color, white)',
  '@media (max-width: 1399px)': {
    padding: theme.spacing(1),
    fontSize: '0.7rem',
  },
}));

const ToggleButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: -20, // Daha fazla dışarı taşı - yarısı sidebar'da yarısı ana içerikte
  top: 55,
  backgroundColor: 'var(--sidebar-color, #102648)',
  color: 'var(--sidebar-text-color, white)',
  '&:hover': {
    backgroundColor: 'var(--sidebar-color, #102648)',
    opacity: 0.9,
  },
  zIndex: 1001, // Daha yüksek z-index - her şeyin üstünde görünsün
  border: '2px solid var(--sidebar-text-color, white)',
  boxShadow: '0 2px 10px rgba(0,0,0,0.3)', // Daha belirgin gölge
  borderRadius: '50%', // Tam yuvarlak buton
  width: 40,
  height: 40,
  transition: theme.transitions.create(['right', 'background-color'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.short,
  }),
  '@media (max-width: 1399px)': {
    right: -18,
    top: 40,
    width: 36,
    height: 36,
    padding: '4px',
  },
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: 240,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: 240,
    boxSizing: 'border-box',
    backgroundColor: 'var(--sidebar-color, #102648)',
    color: 'var(--sidebar-text-color, white)',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.05)',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '2px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(255, 255, 255, 0.3)',
    },
  },
  '@media (max-width: 1399px)': {
    display: 'none',
    '& .MuiDrawer-paper': {
      display: 'none',
    },
  },
}));

// Flyout menü stili (daraltılmış modda hover açılır menü)
const FlyoutMenu = styled(Box)(({ theme }) => ({
  position: 'fixed',
  backgroundColor: 'var(--sidebar-color, #102648)',
  color: 'var(--sidebar-text-color, white)',
  borderRadius: '0 8px 8px 0',
  boxShadow: '4px 4px 20px rgba(0,0,0,0.3)',
  minWidth: 220,
  maxHeight: '80vh',
  overflowY: 'auto',
  zIndex: 1100,
  padding: theme.spacing(1, 0),
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.05)',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '2px',
  },
}));

// Flyout menü öğesi stili
const FlyoutMenuItem = styled(ListItem)<{ active?: boolean }>(({ theme, active }) => ({
  padding: theme.spacing(1.5, 2),
  cursor: 'pointer',
  borderLeft: active ? '4px solid var(--sidebar-text-color, white)' : '4px solid transparent',
  backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
  color: 'var(--sidebar-text-color, white)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
}));

// Flyout başlık stili
const FlyoutHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  marginBottom: theme.spacing(0.5),
}));

// Görev Takip başlığı için style
const GroupHeader = styled(ListItem)<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  padding: theme.spacing(1.5, isCollapsed ? 2 : 3),
  cursor: 'pointer',
  color: 'var(--sidebar-text-color, white)',
  backgroundColor: 'rgba(255,255,255,0.05)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  '@media (max-width: 1399px)': {
    padding: theme.spacing(0.5, isCollapsed ? 0.5 : 1),
    minHeight: 30,
  },
}));

interface SidebarProps {
  openMobile?: boolean;
  onCloseMobile?: () => void;
  onCollapse: (isCollapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ openMobile, onCloseMobile, onCollapse }) => {
  const { logout, userDetails, enabledModules, isModuleEnabled, isSubAdmin, adminPanelPermissions } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common2');
  const tabContext = useTabContextSafe();
  const isDesktopTabs = useMediaQuery('(min-width: 1000px)');
  
  // Responsive tasarım için ekran boyutlarını kontrol et
  const isMediumScreen = useMediaQuery('(max-width: 1399px)');
  const isSmallScreen = useMediaQuery('(max-width: 1399px)');
  
  // İlk yüklemede ve ekran boyutu değiştiğinde sidebar durumunu ayarla
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Küçük ekranlar için varsayılan olarak daraltılmış modu kullan
    return isSmallScreen || isMediumScreen;
  });
  
  // Görev Takip menüsünün açık/kapalı durumu - localStorage'dan al
  const [taskTrackingOpen, setTaskTrackingOpen] = useState(() => {
    const saved = localStorage.getItem(TASK_TRACKING_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });
  
  // Ön Muhasebe menüsünün açık/kapalı durumu - localStorage'dan al
  const [accountingOpen, setAccountingOpen] = useState(() => {
    const saved = localStorage.getItem(ACCOUNTING_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });
  
  // Fatura alt menüsünün açık/kapalı durumu - localStorage'dan al
  const [invoiceOpen, setInvoiceOpen] = useState(() => {
    const saved = localStorage.getItem(INVOICE_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });
  
  // Teklifler alt menüsünün açık/kapalı durumu - localStorage'dan al
  const [offerOpen, setOfferOpen] = useState(() => {
    const saved = localStorage.getItem(OFFER_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });
  
  // Satış Sistemi menüsünün açık/kapalı durumu - localStorage'dan al
  const [salesSystemOpen, setSalesSystemOpen] = useState(() => {
    const saved = localStorage.getItem(SALES_SYSTEM_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });
  
  // Hacıoğlu Export durumu
  const [enableHaciogluExport, setEnableHaciogluExport] = useState(false);

  // WMS menüsünün açık/kapalı durumu - localStorage'dan al
  const [wmsOpen, setWmsOpen] = useState(() => {
    const saved = localStorage.getItem(WMS_OPEN_KEY);
    return saved === null ? false : saved === 'true';
  });
  
  // Cihaz Yönetimi menüsünün açık/kapalı durumu - localStorage'dan al
  const [deviceManagementOpen, setDeviceManagementOpen] = useState(() => {
    const saved = localStorage.getItem('gts_device_management_open');
    return saved === null ? false : saved === 'true';
  });
  
  // İnsan Kaynakları menüsünün açık/kapalı durumu - localStorage'dan al
  const [hrOpen, setHrOpen] = useState(() => {
    const saved = localStorage.getItem(HR_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });
  
  const [cardManagementOpen, setCardManagementOpen] = useState(() => {
    const saved = localStorage.getItem(CARD_MANAGEMENT_OPEN_KEY);
    return saved === null ? false : saved === 'true';
  });

  // Şubeler menüsünün açık/kapalı durumu - localStorage'dan al
  const [branchesOpen, setBranchesOpen] = useState(() => {
    const saved = localStorage.getItem(BRANCHES_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });
  
  // Diğer menüsünün açık/kapalı durumu - localStorage'dan al
  const [otherOpen, setOtherOpen] = useState(() => {
    const saved = localStorage.getItem(OTHER_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });
  
  // Sidebar ayarlar modalını kontrol eden state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  
  // Daraltılmış modda hover flyout menüsü için
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [flyoutPosition, setFlyoutPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [flyoutInitialTop, setFlyoutInitialTop] = useState<number>(0); // Orijinal pozisyon
  const [flyoutMaxHeight, setFlyoutMaxHeight] = useState<number | null>(null);
  const flyoutTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const flyoutRef = React.useRef<HTMLDivElement | null>(null);
  
  // Ana başlıkların görünürlüğü — kullanıcı tercihi (localStorage, SidebarSettingsModal)
  const [userVisibleSections, setUserVisibleSections] = useState<{[key: string]: boolean}>({
    task_tracking: true,
    hr: true,
    accounting: true,
    sales_system: true,
    wms: true,
    device_management: true,
    card_management: true,
    branches: true,
    other: true
  });

  // Efektif görünürlük = kullanıcı tercihi VE şirket modülü açık (SuperAdmin modül kontrolü)
  // VE (subadmin ise) adminPanel izin haritasında en az bir buton izinli (deny-by-default).
  const visibleSections = React.useMemo(() => {
    const out: {[key: string]: boolean} = {};
    for (const key of Object.keys(userVisibleSections)) {
      out[key] =
        userVisibleSections[key] &&
        isModuleEnabled(key) &&
        (!isSubAdmin || isSectionAllowedForSubAdmin(adminPanelPermissions, key));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userVisibleSections, enabledModules, isSubAdmin, adminPanelPermissions]);

  const hasAccessibleReports = React.useMemo(
    () => getAccessibleReports({ enabledModules, isSubAdmin, adminPanelPermissions }).length > 0,
    [enabledModules, isSubAdmin, adminPanelPermissions]
  );
  
  // Alt başlıkların görünürlüğünü takip etmek için 
  const [modalClosed, setModalClosed] = useState(false);
  
  // Ekran boyutu değiştiğinde sidebar durumunu güncelle
  useEffect(() => {
    // Orta boyut ekranlar için daraltılmış mod
    if (isMediumScreen && !isSmallScreen) {
      setIsCollapsed(true);
      onCollapse(true);
    }
    // Mobil ekranlar için işlemi değiştirmeye gerek yok, çünkü zaten display: none ile gizleniyor
  }, [isSmallScreen, isMediumScreen, onCollapse]);

  // Storage event'lerini dinle (QuickStart sayfasından gelen değişiklikler için)
  useEffect(() => {
    const handleStorageChange = () => {
      // localStorage'dan güncel durumları oku
      setTaskTrackingOpen(localStorage.getItem(TASK_TRACKING_OPEN_KEY) === 'true');
      setAccountingOpen(localStorage.getItem(ACCOUNTING_OPEN_KEY) === 'true');
      setInvoiceOpen(localStorage.getItem(INVOICE_OPEN_KEY) === 'true');
      setOfferOpen(localStorage.getItem(OFFER_OPEN_KEY) === 'true');
      setSalesSystemOpen(localStorage.getItem(SALES_SYSTEM_OPEN_KEY) === 'true');
      setWmsOpen(localStorage.getItem(WMS_OPEN_KEY) === 'true');
      setDeviceManagementOpen(localStorage.getItem('gts_device_management_open') === 'true');
      setCardManagementOpen(localStorage.getItem(CARD_MANAGEMENT_OPEN_KEY) === 'true');
      setHrOpen(localStorage.getItem(HR_OPEN_KEY) === 'true');
      setBranchesOpen(localStorage.getItem(BRANCHES_OPEN_KEY) === 'true');
      setOtherOpen(localStorage.getItem(OTHER_OPEN_KEY) === 'true');
    };

    // Event listener ekle
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Scroll to section event'ini dinle (QuickStart sayfasından gelen scroll komutları için)
  useEffect(() => {
    const handleScrollToSection = (event: any) => {
      const sectionId = event.detail?.sectionId;
      if (!sectionId) return;

      // Bölüm ID'sine göre DOM element'ini bul
      let sectionElement: HTMLElement | null = null;
      
      // ID'ye göre hangi başlığa scroll edileceğini belirle
      const sectionMap: { [key: string]: string } = {
        'task_tracking': 'group-header-task-tracking',
        'hr': 'group-header-hr',
        'accounting': 'group-header-accounting',
        'wms': 'group-header-wms',
        'sales_system': 'group-header-sales-system',
        'device_management': 'group-header-device-management',
        'branches': 'group-header-branches',
        'other': 'group-header-other'
      };

      const elementId = sectionMap[sectionId];
      if (elementId) {
        sectionElement = document.getElementById(elementId);
      }

      // Element bulunursa, yumuşak scroll yap
      if (sectionElement) {
        sectionElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' // Orta noktaya hizala
        });
      }
    };

    // Custom event listener ekle
    window.addEventListener('scrollToSection', handleScrollToSection);

    // Cleanup
    return () => {
      window.removeEventListener('scrollToSection', handleScrollToSection);
    };
  }, []);

  // İlk yüklemede sidebar ve yazı rengini CSS değişkeni olarak ayarla
  useEffect(() => {
    const savedColor = localStorage.getItem(SIDEBAR_COLOR_KEY) || DEFAULT_SIDEBAR_COLOR;
    const savedTextColor = localStorage.getItem('gts_sidebar_text_color') || '#ffffff';
    document.documentElement.style.setProperty('--sidebar-color', savedColor);
    document.documentElement.style.setProperty('--sidebar-text-color', savedTextColor);
  }, []);

  // İlk yüklemede buton görünürlük ayarlarını uygula
  useEffect(() => {
    const savedVisibleButtons = localStorage.getItem(SIDEBAR_VISIBLE_BUTTONS_KEY);
    if (savedVisibleButtons) {
      const visibleButtonIds = JSON.parse(savedVisibleButtons);
      SIDEBAR_BUTTONS.forEach(button => {
        const isVisible = visibleButtonIds.includes(button.id);
        document.documentElement.style.setProperty(
          `--sidebar-button-${button.id}-display`, 
          isVisible ? 'flex' : 'none'
        );
      });
    } else {
      // Eğer ayar yoksa, tüm butonları görünür yap
      SIDEBAR_BUTTONS.forEach(button => {
        document.documentElement.style.setProperty(
          `--sidebar-button-${button.id}-display`, 
          'flex'
        );
      });
    }
  }, []);

  // İlk yüklemede ve modalın her kapatılmasında localStorage'ı kontrol et
  useEffect(() => {
    const savedHeadersStr = localStorage.getItem(SIDEBAR_VISIBLE_HEADERS_KEY);
    if (savedHeadersStr) {
      try {
        const savedHeaders = JSON.parse(savedHeadersStr);
        const newVisibleSections = {
          task_tracking: savedHeaders.includes('task_tracking'),
          hr: savedHeaders.includes('hr'),
          accounting: savedHeaders.includes('accounting'),
          sales_system: savedHeaders.includes('sales_system'),
          wms: savedHeaders.includes('wms'),
          device_management: savedHeaders.includes('device_management'),
          card_management: savedHeaders.includes('card_management'),
          branches: savedHeaders.includes('branches'),
          other: savedHeaders.includes('other')
        };
        setUserVisibleSections(newVisibleSections);
      } catch (error) {
        console.error('Header görünürlük ayarları yüklenemedi:', error);
      }
    }
  }, [modalClosed]);

  // Hacıoğlu Export durumunu kontrol et
  useEffect(() => {
    const checkHaciogluStatus = async () => {
      if (userDetails?.companyId) {
        try {
          const response = await mainApi.get(`/api/Company`);
          const data = response.data;
          setEnableHaciogluExport(data.enableHaciogluExport || false);
          console.log('[Sidebar] Hacıoğlu durumu:', data.enableHaciogluExport);
        } catch (error) {
          console.warn('[Sidebar] Hacıoğlu durumu kontrol edilemedi (API henüz yüklenmemiş olabilir):', error);
          // Hata durumunda false döndür (menüyü gizle)
          setEnableHaciogluExport(false);
        }
      } else {
        setEnableHaciogluExport(false);
      }
    };

    checkHaciogluStatus();
  }, [userDetails?.companyId]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Formdan ayrılma onayı bekleyen hedef sayfa
  const [leaveConfirmPath, setLeaveConfirmPath] = useState<string | null>(null);

  const doNavigate = (path: string) => {
    if (isDesktopTabs && tabContext && ROUTE_COMPONENT_MAP[path]) {
      const entry = ROUTE_COMPONENT_MAP[path];
      const localizedTitle =
        path === '/fiyat-degisim-loglari'
          ? t('accounting:stock.priceChangeLogs.title')
          : path === '/kasa' || path === '/accounting/cashbox'
            ? t('accounting:cash.title')
            : path === '/accounting/cashbox/transactions'
              ? t('accounting:cash.transactionReport.title')
              : path === '/banka'
                ? t('accounting:bank.title')
          : entry.title;
      tabContext.openTab(path, localizedTitle);
    } else {
      navigate(path);
    }
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  const handleNavigation = (path: string) => {
    // Dolu bir form sayfasından (fatura/teklif/sipariş/irsaliye) ayrılmadan önce onay iste
    if (getTransientRouteTitle(location.pathname) && isFormDirty() && path !== location.pathname) {
      setLeaveConfirmPath(path);
      return;
    }
    doNavigate(path);
  };

  const handleLogout = async () => {
    try {
      tabContext?.clearUnpinnedTabs();
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Çıkış yapılırken hata oluştu:', error);
    }
  };

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapse(newCollapsedState);
  };

  // Görev Takip menüsünü aç/kapat
  const toggleTaskTracking = () => {
    const newState = !taskTrackingOpen;
    setTaskTrackingOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(TASK_TRACKING_OPEN_KEY, String(newState));
  };
  
  // Ön Muhasebe menüsünü aç/kapat
  const toggleAccounting = () => {
    const newState = !accountingOpen;
    setAccountingOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(ACCOUNTING_OPEN_KEY, String(newState));
  };
  
  // Fatura alt menüsünü aç/kapat
  const toggleInvoice = (e: React.MouseEvent) => {
    e.stopPropagation(); // Ana menünün toggle olmasını engelle
    const newState = !invoiceOpen;
    setInvoiceOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(INVOICE_OPEN_KEY, String(newState));
  };
  
  // Teklifler alt menüsünü aç/kapat
  const toggleOffer = (e: React.MouseEvent) => {
    e.stopPropagation(); // Ana menünün toggle olmasını engelle
    const newState = !offerOpen;
    setOfferOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(OFFER_OPEN_KEY, String(newState));
  };

  // Satış Sistemi menüsünü aç/kapat
  const toggleSalesSystem = () => {
    const newState = !salesSystemOpen;
    setSalesSystemOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(SALES_SYSTEM_OPEN_KEY, String(newState));
  };

  // WMS menüsünü aç/kapat
  const toggleWms = () => {
    const newState = !wmsOpen;
    setWmsOpen(newState);
    localStorage.setItem(WMS_OPEN_KEY, String(newState));
  };

  // Cihaz Yönetimi menüsünü aç/kapat
  const toggleDeviceManagement = () => {
    const newState = !deviceManagementOpen;
    setDeviceManagementOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem('gts_device_management_open', String(newState));
  };

  // İnsan Kaynakları menüsünü aç/kapat
  const toggleHr = () => {
    const newState = !hrOpen;
    setHrOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(HR_OPEN_KEY, String(newState));
  };

  const toggleCardManagement = () => {
    const newState = !cardManagementOpen;
    setCardManagementOpen(newState);
    localStorage.setItem(CARD_MANAGEMENT_OPEN_KEY, String(newState));
  };

  // Şubeler menüsünü aç/kapat
  const toggleBranches = () => {
    const newState = !branchesOpen;
    setBranchesOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(BRANCHES_OPEN_KEY, String(newState));
  };

  // Diğer menüsünü aç/kapat
  const toggleOther = () => {
    const newState = !otherOpen;
    setOtherOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(OTHER_OPEN_KEY, String(newState));
  };

  // Modal açma/kapama işlevi
  const handleSettingsModal = () => {
    if (settingsModalOpen) {
      // Modal kapanıyor, localStorage'dan güncel ayarları oku
      setModalClosed(!modalClosed); // Trigger effect
    }
    setSettingsModalOpen(!settingsModalOpen);
  };

  // Flyout menü hover işleyicileri
  const handleSectionMouseEnter = (sectionId: string, event: React.MouseEvent<HTMLElement>) => {
    if (!isCollapsed) return; // Sadece daraltılmış modda çalışsın
    
    // Önceki timeout'u temizle
    if (flyoutTimeoutRef.current) {
      clearTimeout(flyoutTimeoutRef.current);
      flyoutTimeoutRef.current = null;
    }
    
    // Element'in pozisyonunu al
    const rect = event.currentTarget.getBoundingClientRect();
    const initialTop = rect.top;
    
    // Orijinal pozisyonu kaydet ve başlangıç pozisyonunu ayarla
    setFlyoutInitialTop(initialTop);
    setFlyoutPosition({
      top: initialTop,
      left: 70 // Sidebar genişliği (daraltılmış)
    });
    setFlyoutMaxHeight(null); // Reset max height
    setHoveredSection(sectionId);
  };

  // Flyout pozisyonunu ayarla - ekranın altına taşmasını engelle
  React.useEffect(() => {
    if (hoveredSection) {
      // Double requestAnimationFrame - DOM'un kesinlikle güncellendiğinden emin ol
      // İlk frame: React DOM'u günceller
      // İkinci frame: Tarayıcı layout hesaplar
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!flyoutRef.current) return;
          
          const flyoutRect = flyoutRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const padding = 16; // Ekran kenarından boşluk
          
          // Eğer flyout ekranın altına taşıyorsa
          if (flyoutRect.bottom > viewportHeight - padding) {
            const overflow = flyoutRect.bottom - (viewportHeight - padding);
            const newTop = flyoutInitialTop - overflow;
            
            // Eğer yukarı kaydırma sonucu üstten taşacaksa, max height uygula
            if (newTop < padding) {
              setFlyoutPosition(prev => ({ ...prev, top: padding }));
              setFlyoutMaxHeight(viewportHeight - (padding * 2));
            } else {
              setFlyoutPosition(prev => ({ ...prev, top: newTop }));
              setFlyoutMaxHeight(null);
            }
          } else {
            // Taşmıyorsa orijinal pozisyonda kal
            setFlyoutPosition(prev => ({ ...prev, top: flyoutInitialTop }));
            setFlyoutMaxHeight(null);
          }
        });
      });
    }
  }, [hoveredSection, flyoutInitialTop]);

  const handleSectionMouseLeave = () => {
    if (!isCollapsed) return;
    
    // Kısa bir gecikme ile kapat (flyout'a geçiş için)
    flyoutTimeoutRef.current = setTimeout(() => {
      setHoveredSection(null);
    }, 150);
  };

  const handleFlyoutMouseEnter = () => {
    // Flyout'a girildiğinde timeout'u iptal et
    if (flyoutTimeoutRef.current) {
      clearTimeout(flyoutTimeoutRef.current);
      flyoutTimeoutRef.current = null;
    }
  };

  const handleFlyoutMouseLeave = () => {
    // Flyout'tan çıkıldığında kapat
    setHoveredSection(null);
  };

  // QuickStart flyout butonlarına tıklama - sidebar menüsünü aç
  const handleQuickStartButtonClick = (button: typeof QUICK_START_BUTTONS[0]) => {
    // Raporlar için özel davranış - direkt sayfaya git
    if (button.id === 'reports') {
      handleNavigation('/reports');
      setHoveredSection(null);
      return;
    }

    // Diğer butonlar için sidebar menüsünü aç
    if (button.storageKey) {
      // Tüm menüleri kapat
      const allKeys = [
        'gts_task_tracking_open',
        'gts_hr_open',
        'gts_accounting_open',
        'gts_sales_system_open',
        'gts_device_management_open',
        'gts_branches_open',
        'gts_other_open'
      ];
      allKeys.forEach(key => {
        if (key !== button.storageKey) {
          localStorage.setItem(key, 'false');
        }
      });

      // Bu menüyü aç
      localStorage.setItem(button.storageKey, 'true');
      
      // Sidebar'ı güncelle
      window.dispatchEvent(new Event('storage'));

      // İlgili state'i güncelle
      switch (button.id) {
        case 'task_tracking':
          setTaskTrackingOpen(true);
          break;
        case 'hr':
          setHrOpen(true);
          break;
        case 'accounting':
          setAccountingOpen(true);
          break;
        case 'sales_system':
          setSalesSystemOpen(true);
          break;
        case 'device_management':
          setDeviceManagementOpen(true);
          break;
        case 'branches':
          setBranchesOpen(true);
          break;
      }

      // Sidebar'ı genişlet
      if (isCollapsed) {
        toggleSidebar();
      }

      // Scroll to section
      setTimeout(() => {
        const scrollEvent = new CustomEvent('scrollToSection', { 
          detail: { sectionId: button.id } 
        });
        window.dispatchEvent(scrollEvent);
      }, 100);
    }

    setHoveredSection(null);
  };

  const getLegacyAliases = (sectionId: string, itemId: string): string[] => {
    if (sectionId === 'accounting') {
      const map: Record<string, string[]> = {
        dashboard: ['accounting-dashboard'],
        current: ['cari'],
        stock: ['stok'],
        bank: ['banka'],
        cash: ['kasa'],
        warehouse: ['depo'],
        currency: ['doviz'],
        checksNotes: ['cek-senet'],
        loans: ['krediler', 'kredi-odemeleri'],
        projects: ['projeler'],
        invoice: ['fatura'],
        eInvoice: ['e-fatura'],
        offers: ['teklifler'],
        waybills: ['irsaliyeler'],
        orders: ['siparisler'],
      };
      return map[itemId] || [];
    }
    if (sectionId === 'sales_system') {
      const map: Record<string, string[]> = {
        fastSale: ['hizli-satis'],
        restaurant: ['restorant'],
        qrMenu: ['qr-menu'],
        eCommerce: ['e-commerce'],
      };
      return map[itemId] || [];
    }
    if (sectionId === 'hr') {
      const map: Record<string, string[]> = {
        leaveManagement: ['ik-izin'],
        performanceManagement: ['ik-performans'],
        trainingDevelopment: ['ik-egitim'],
        payroll: ['ik-bordro'],
        documentManagement: ['ik-dokuman'],
        recruitment: ['ik-isealim'],
      };
      return map[itemId] || [];
    }
    return [];
  };

  // Yeni eklenen sidebar ogeleri — mevcut kullanicilarin kayitli listesinde
  // bulunmasa bile default olarak gorunmesi gerekenler. Boylece eski
  // kullanicilar yeni ozelliklerden mahrum kalmaz; istemiyorlarsa
  // SidebarSettingsModal'dan kapatabilirler.
  const NEW_DEFAULT_VISIBLE_ITEMS: Record<string, string[]> = {
    accounting: ['loans', 'projects', 'interCompanyB2B'],
    wms: ['wmsCancelMgmt'],
    other: ['mailReports', 'users'],
    card_management: ['accessManagement'],
    device_management: ['scales'],
    hr: ['pdks'],
  };

  // Bir öğenin görünür olup olmadığını kontrol eden yardımcı fonksiyon
  const isItemVisible = (sectionId: string, itemId: string) => {
    // Çoklu admin sistemi: subadmin deny-by-default — izin haritasında açıkça
    // işaretlenmemiş her buton gizlenir. 'Kullanıcılar' butonu subadmin'de asla görünmez.
    if (isSubAdmin) {
      if (sectionId === 'other' && itemId === 'users') return false;
      if (!isItemAllowedForSubAdmin(adminPanelPermissions, sectionId, itemId)) return false;
    }
    try {
      const savedItemsStr = localStorage.getItem(SIDEBAR_VISIBLE_ITEMS_KEY);
      if (savedItemsStr) {
        const savedItems = JSON.parse(savedItemsStr);
        const sectionItems: string[] | undefined = savedItems[sectionId];
        if (!sectionItems) return true;
        const aliases = getLegacyAliases(sectionId, itemId);
        if ([itemId, ...aliases].some(id => sectionItems.includes(id))) {
          return true;
        }
        // Kayitli liste var ama bu yeni eklenen oge listede degil → default goster
        if (NEW_DEFAULT_VISIBLE_ITEMS[sectionId]?.includes(itemId)) {
          return true;
        }
        return false;
      }
      return true;
    } catch (error) {
      console.error('Item görünürlük kontrolü yapılamadı:', error);
      return true;
    }
  };

  // Menü öğelerini render etme fonksiyonu - butonları tek bir yerden oluşturmak için
  const renderMenuItem = (path: string, label: string, icon: React.ReactNode, buttonId: string) => {
    return (
      <MenuItem 
        active={isActive(path)} 
        onClick={() => handleNavigation(path)}
        isCollapsed={isCollapsed}
        sx={{ display: `var(--sidebar-button-${buttonId}-display, flex)` }}
      >
        <ListItemIcon sx={{ 
          color: 'var(--sidebar-text-color, white)', 
          minWidth: isSmallScreen ? 16 : 40,
          marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
        }}>
          {icon}
        </ListItemIcon>
        {!isCollapsed && (
          <ListItemText 
            primary={label} 
            primaryTypographyProps={{ 
              fontSize: isSmallScreen ? '0.65rem' : 'inherit',
              noWrap: true
            }} 
            sx={{ margin: isSmallScreen ? 0 : 'auto' }}
          />
        )}
      </MenuItem>
    );
  };

  return (
    <SidebarContainer isCollapsed={isCollapsed}>
      <ToggleButton onClick={toggleSidebar} size={isSmallScreen ? "small" : "medium"}>
        {isCollapsed ? <ChevronRightIcon fontSize={isSmallScreen ? "small" : "medium"} /> : <ChevronLeftIcon fontSize={isSmallScreen ? "small" : "medium"} />}
      </ToggleButton>
      
      <Logo isCollapsed={isCollapsed}>
        <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LogoImage src={BRAND.logos.main} alt={`${BRAND.name} Logo`} isCollapsed={isCollapsed} />
          {!isCollapsed && (
            <IconButton 
              onClick={handleSettingsModal}
              sx={{ 
                position: 'absolute', 
                left: 5, 
                top: 5, 
                color: 'var(--sidebar-text-color, white)',
                p: isSmallScreen ? 0.5 : 1
              }}
            >
              <SettingsIcon fontSize={isSmallScreen ? "small" : "medium"} />
            </IconButton>
          )}
        </Box>
        {!isCollapsed && (
          <Typography 
            variant={isSmallScreen ? "subtitle1" : "h6"} 
            sx={{ 
              color: 'var(--sidebar-text-color, white)',
              fontWeight: 'bold'
            }}
          >
            {BRAND.copyrightHolder}
          </Typography>
        )}
      </Logo>
      
      <MenuContainer>
        {/* Yönetici Ekranı Butonu - Tek buton, başlık değil.
            Subadmin'de ayrı izne tabidir (admin_screen.adminScreen) — izin yoksa gizli. */}
        {(!isSubAdmin || isItemAllowedForSubAdmin(adminPanelPermissions, 'admin_screen', 'adminScreen')) && (
        <MenuItem 
          active={isActive('/accounting/dashboard')} 
          onClick={() => handleNavigation('/accounting/dashboard')}
          isCollapsed={isCollapsed}
          onMouseEnter={(e) => handleSectionMouseEnter('quick_start', e)}
          onMouseLeave={handleSectionMouseLeave}
          sx={{ 
            mb: 1,
            backgroundColor: isActive('/accounting/dashboard') 
              ? 'rgba(255,255,255,0.1)' 
              : 'rgba(76, 175, 80, 0.1)', // Hafif yeşil arka plan
            '&:hover': {
              backgroundColor: isActive('/accounting/dashboard')
                ? 'rgba(255,255,255,0.15)'
                : 'rgba(76, 175, 80, 0.2)',
            },
          }}
        >
          <ListItemIcon sx={{ 
            color: 'var(--sidebar-text-color, white)', 
            minWidth: isSmallScreen ? 16 : 40,
            marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
          }}>
            <RocketLaunchIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText 
              primary={t('menu.adminScreen', { ns: 'accounting' })}
              primaryTypographyProps={{ 
                fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                fontWeight: 'bold',
                noWrap: true
              }}
              sx={{ margin: isSmallScreen ? 0 : 'auto' }}
            />
          )}
        </MenuItem>
        )}

        {/* Raporlar — tüm raporların kartlarla listelendiği tek sayfa */}
        {hasAccessibleReports && (
        <Tooltip title={isCollapsed ? t('sidebar.reportsHub') : ''} placement="right" arrow>
        <MenuItem
          active={isActive('/raporlar')}
          onClick={() => handleNavigation('/raporlar')}
          isCollapsed={isCollapsed}
          sx={{
            mb: 1,
            backgroundColor: isActive('/raporlar')
              ? 'rgba(255,255,255,0.1)'
              : 'rgba(33, 150, 243, 0.12)',
            '&:hover': {
              backgroundColor: isActive('/raporlar')
                ? 'rgba(255,255,255,0.15)'
                : 'rgba(33, 150, 243, 0.22)',
            },
          }}
        >
          <ListItemIcon sx={{
            color: 'var(--sidebar-text-color, white)',
            minWidth: isSmallScreen ? 16 : 40,
            marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
          }}>
            <InsightsIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary={t('sidebar.reportsHub')}
              primaryTypographyProps={{
                fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                fontWeight: 'bold',
                noWrap: true
              }}
              sx={{ margin: isSmallScreen ? 0 : 'auto' }}
            />
          )}
        </MenuItem>
        </Tooltip>
        )}
        
        {/* Ön Muhasebe Başlığı */}
        {visibleSections.accounting && (
          <>
            <GroupHeader 
              id="group-header-accounting"
              onClick={toggleAccounting} 
              isCollapsed={isCollapsed}
              onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
              onMouseLeave={handleSectionMouseLeave}
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('menu.preAccounting', { ns: 'accounting' })}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      fontWeight: 'bold',
                      noWrap: true
                    }}
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </Box>
              {!isCollapsed && (accountingOpen ? <ExpandLess /> : <ExpandMore />)}
            </GroupHeader>
            
            {/* Ön Muhasebe altındaki öğeler */}
            <Collapse in={accountingOpen} timeout="auto" unmountOnExit>
              {/* Normal butonlar - Depo, Döviz, Şube Fiyatları ve Resim Kütüphanesi hariç */}
              {ACCOUNTING_BUTTONS.filter(button => button.id !== 'warehouse' && button.id !== 'currency' && button.id !== 'branchPrices' && button.id !== 'imageLibrary').map((button) => (
                isItemVisible('accounting', button.id) && (
                  <MenuItem 
                    key={button.id}
                    active={isActive(button.path)} 
                    onClick={() => handleNavigation(button.path)}
                    isCollapsed={isCollapsed}
                    onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                    onMouseLeave={handleSectionMouseLeave}
                    sx={{ 
                      paddingLeft: isCollapsed ? 2 : 4
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: 'var(--sidebar-text-color, white)', 
                      minWidth: isSmallScreen ? 16 : 40,
                      marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                    }}>
                      {button.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={t(`menu.accountingButtons.${button.id}`, {
                          ns: 'accounting',
                          defaultValue: t(`sidebar.accountingButtons.${button.id}`)
                        })} 
                        primaryTypographyProps={{ 
                          fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                          noWrap: true
                        }} 
                        sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                      />
                    )}
                  </MenuItem>
                )
              ))}
               
              {/* Çek/Senet */}
              {isItemVisible('accounting', 'checksNotes') && (
              <MenuItem 
                active={isActive('/cek-senet')}
                isCollapsed={isCollapsed}
                onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                onMouseLeave={handleSectionMouseLeave}
                sx={{ 
                  paddingLeft: isCollapsed ? 2 : 4
                }}
                onClick={() => handleNavigation('/cek-senet')}
              >
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <ReceiptLongIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.checksNotes')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      noWrap: true
                    }} 
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </MenuItem>
              )}

              {/* Krediler */}
              {isItemVisible('accounting', 'loans') && (
              <MenuItem 
                active={isActive('/krediler')}
                isCollapsed={isCollapsed}
                onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                onMouseLeave={handleSectionMouseLeave}
                sx={{ 
                  paddingLeft: isCollapsed ? 2 : 4
                }}
                onClick={() => handleNavigation('/krediler')}
              >
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <RequestQuoteIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.loans')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      noWrap: true
                    }} 
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </MenuItem>
              )}

              {/* Projeler (PYS) */}
              {isItemVisible('accounting', 'projects') && (
              <MenuItem 
                active={isActive('/projeler')}
                isCollapsed={isCollapsed}
                onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                onMouseLeave={handleSectionMouseLeave}
                sx={{ 
                  paddingLeft: isCollapsed ? 2 : 4
                }}
                onClick={() => handleNavigation('/projeler')}
              >
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <AccountTreeIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.projects')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      noWrap: true
                    }} 
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </MenuItem>
              )}
              
              {/* Fatura Başlığı */}
              {isItemVisible('accounting', 'invoice') && (
              <MenuItem 
                active={isActive('/fatura/alis')}
                isCollapsed={isCollapsed}
                onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                onMouseLeave={handleSectionMouseLeave}
                sx={{ 
                  paddingLeft: isCollapsed ? 2 : 4,
                  justifyContent: 'space-between',
                  display: 'flex'
                }}
                onClick={() => handleNavigation('/fatura/alis')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ 
                    color: 'var(--sidebar-text-color, white)', 
                    minWidth: isSmallScreen ? 16 : 40,
                    marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                  }}>
                    <ReceiptIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={t('sidebar.invoice')}
                      primaryTypographyProps={{ 
                        fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                        fontWeight: 'medium',
                        noWrap: true
                      }} 
                      sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                    />
                  )}
                </Box>
              </MenuItem>
              )}
              
              {/* E-Fatura Başlığı */}
              {isItemVisible('accounting', 'eInvoice') && (
              <MenuItem 
                active={isActive('/e-fatura')}
                isCollapsed={isCollapsed}
                onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                onMouseLeave={handleSectionMouseLeave}
                sx={{ 
                  paddingLeft: isCollapsed ? 2 : 4,
                  justifyContent: 'space-between',
                  display: 'flex'
                }}
                onClick={() => handleNavigation('/e-fatura')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ 
                    color: 'var(--sidebar-text-color, white)', 
                    minWidth: isSmallScreen ? 16 : 40,
                    marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                  }}>
                    <DescriptionIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={t('menu.eInvoice', { ns: 'accounting' })}
                      primaryTypographyProps={{ 
                        fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                        fontWeight: 'medium',
                        noWrap: true
                      }} 
                      sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                    />
                  )}
                </Box>
              </MenuItem>
              )}
              
              {/* Teklifler Başlığı */}
              {isItemVisible('accounting', 'offers') && (
              <MenuItem 
                active={isActive('/teklifler')}
                isCollapsed={isCollapsed}
                onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                onMouseLeave={handleSectionMouseLeave}
                sx={{ 
                  paddingLeft: isCollapsed ? 2 : 4,
                  justifyContent: 'space-between',
                  display: 'flex'
                }}
                onClick={() => handleNavigation('/teklifler')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ 
                    color: 'var(--sidebar-text-color, white)', 
                    minWidth: isSmallScreen ? 16 : 40,
                    marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                  }}>
                    <LocalOfferIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={t('sidebar.offers')}
                      primaryTypographyProps={{ 
                        fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                        fontWeight: 'medium',
                        noWrap: true
                      }} 
                      sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                    />
                  )}
                </Box>
              </MenuItem>
              )}
              
              {/* İrsaliyeler Başlığı */}
              {isItemVisible('accounting', 'waybills') && (
              <MenuItem 
                active={isActive('/irsaliyeler')}
                isCollapsed={isCollapsed}
                onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                onMouseLeave={handleSectionMouseLeave}
                sx={{ 
                  paddingLeft: isCollapsed ? 2 : 4,
                  justifyContent: 'space-between',
                  display: 'flex'
                }}
                onClick={() => handleNavigation('/irsaliyeler')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ 
                    color: 'var(--sidebar-text-color, white)', 
                    minWidth: isSmallScreen ? 16 : 40,
                    marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                  }}>
                    <LocalShippingIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={t('sidebar.waybills')}
                      primaryTypographyProps={{ 
                        fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                        fontWeight: 'medium',
                        noWrap: true
                      }} 
                      sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                    />
                  )}
                </Box>
              </MenuItem>
              )}
              
              {/* Siparişler Başlığı */}
              {isItemVisible('accounting', 'orders') && (
              <MenuItem 
                active={isActive('/siparisler')}
                isCollapsed={isCollapsed}
                onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                onMouseLeave={handleSectionMouseLeave}
                sx={{ 
                  paddingLeft: isCollapsed ? 2 : 4,
                  justifyContent: 'space-between',
                  display: 'flex'
                }}
                onClick={() => handleNavigation('/siparisler')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon sx={{ 
                    color: 'var(--sidebar-text-color, white)', 
                    minWidth: isSmallScreen ? 16 : 40,
                    marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                  }}>
                    <ShoppingCartIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                  </ListItemIcon>
                  {!isCollapsed && (
                    <ListItemText 
                      primary={t('sidebar.orders')}
                      primaryTypographyProps={{ 
                        fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                        fontWeight: 'medium',
                        noWrap: true
                      }} 
                      sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                    />
                  )}
                </Box>
              </MenuItem>
              )}
              
              {/* Depo, Döviz, Şube Fiyatları ve Resim Kütüphanesi butonları en altta */}
              {ACCOUNTING_BUTTONS.filter(button => button.id === 'warehouse' || button.id === 'currency' || button.id === 'branchPrices' || button.id === 'imageLibrary').map((button) => (
                isItemVisible('accounting', button.id) && (
                  <MenuItem 
                    key={button.id}
                    active={isActive(button.path)} 
                    onClick={() => handleNavigation(button.path)}
                    isCollapsed={isCollapsed}
                    onMouseEnter={(e) => handleSectionMouseEnter('accounting', e)}
                    onMouseLeave={handleSectionMouseLeave}
                    sx={{ 
                      paddingLeft: isCollapsed ? 2 : 4
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: 'var(--sidebar-text-color, white)', 
                      minWidth: isSmallScreen ? 16 : 40,
                      marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                    }}>
                      {button.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={t(`menu.accountingButtons.${button.id}`, {
                          ns: 'accounting',
                          defaultValue: t(`sidebar.accountingButtons.${button.id}`)
                        })} 
                        primaryTypographyProps={{ 
                          fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                          noWrap: true
                        }} 
                        sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                      />
                    )}
                  </MenuItem>
                )
              ))}
            </Collapse>
          </>
        )}
        
        {/* Satış Sistemi Başlığı */}
        {visibleSections.sales_system && (
          <>
            <GroupHeader 
              id="group-header-sales-system"
              onClick={toggleSalesSystem} 
              isCollapsed={isCollapsed}
              onMouseEnter={(e) => handleSectionMouseEnter('sales_system', e)}
              onMouseLeave={handleSectionMouseLeave}
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <StorefrontIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.salesSystem')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      fontWeight: 'bold',
                      noWrap: true
                    }}
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </Box>
              {!isCollapsed && (salesSystemOpen ? <ExpandLess /> : <ExpandMore />)}
            </GroupHeader>
            
            {/* Satış Sistemi altındaki butonlar */}
            <Collapse in={salesSystemOpen} timeout="auto" unmountOnExit>
              {SALES_SYSTEM_BUTTONS.map((button: any) => {
                // Hacıoğlu entegrasyonu gerektiren menü öğelerini kontrol et
                if (button.requireHacioglu && !enableHaciogluExport) {
                  return null;
                }
                return isItemVisible('sales_system', button.id) && (
                  <MenuItem 
                    key={button.id}
                    active={isActive(button.path)} 
                    onClick={() => handleNavigation(button.path)}
                    isCollapsed={isCollapsed}
                    onMouseEnter={(e) => handleSectionMouseEnter('sales_system', e)}
                    onMouseLeave={handleSectionMouseLeave}
                    sx={{ 
                      paddingLeft: isCollapsed ? 2 : 4
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: 'var(--sidebar-text-color, white)', 
                      minWidth: isSmallScreen ? 16 : 40,
                      marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                    }}>
                      {button.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                      primary={t(`sidebar.salesSystemButtons.${button.id}`)}                       
                      primaryTypographyProps={{ 
                          fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                          noWrap: true
                        }} 
                        sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                      />
                    )}
                  </MenuItem>
                );
              })}
            </Collapse>
          </>
        )}
        
        {/* Görev Takip Başlığı */}
        {visibleSections.task_tracking && (
          <>
            <GroupHeader 
              id="group-header-task-tracking"
              onClick={toggleTaskTracking} 
              isCollapsed={isCollapsed}
              onMouseEnter={(e) => handleSectionMouseEnter('task_tracking', e)}
              onMouseLeave={handleSectionMouseLeave}
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <TrackChangesIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.taskTracking')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      fontWeight: 'bold',
                      noWrap: true
                    }}
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </Box>
              {!isCollapsed && (taskTrackingOpen ? <ExpandLess /> : <ExpandMore />)}
            </GroupHeader>
            
            {/* Görev Takip altındaki butonlar */}
            <Collapse in={taskTrackingOpen} timeout="auto" unmountOnExit>
              {TASK_TRACKING_BUTTONS.map((button) => (
                isItemVisible('task_tracking', button.id) && (
                  <MenuItem 
                    key={button.id}
                    active={isActive(button.path)} 
                    onClick={() => handleNavigation(button.path)}
                    isCollapsed={isCollapsed}
                    onMouseEnter={(e) => handleSectionMouseEnter('task_tracking', e)}
                    onMouseLeave={handleSectionMouseLeave}
                    sx={{ 
                      paddingLeft: isCollapsed ? 2 : 4
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: 'var(--sidebar-text-color, white)', 
                      minWidth: isSmallScreen ? 16 : 40,
                      marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                    }}>
                      {button.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={t(`sidebar.taskTrackingButtons.${button.id}`)} 
                        primaryTypographyProps={{ 
                          fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                          noWrap: true
                        }} 
                        sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                      />
                    )}
                  </MenuItem>
                )
              ))}
            </Collapse>
          </>
        )}
        
        {/* İnsan Kaynakları Başlığı */}
        {visibleSections.hr && (
          <>
            <GroupHeader 
              id="group-header-hr"
              onClick={toggleHr} 
              isCollapsed={isCollapsed}
              onMouseEnter={(e) => handleSectionMouseEnter('hr', e)}
              onMouseLeave={handleSectionMouseLeave}
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <SupervisedUserCircleIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.humanResources')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      fontWeight: 'bold',
                      noWrap: true
                    }}
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </Box>
              {!isCollapsed && (hrOpen ? <ExpandLess /> : <ExpandMore />)}
            </GroupHeader>
            
            {/* İnsan Kaynakları altındaki butonlar */}
            <Collapse in={hrOpen} timeout="auto" unmountOnExit>
              {HR_BUTTONS.length > 0 ? (
                HR_BUTTONS.map((button) => (
                  !TEMP_HIDDEN_HR_BUTTON_IDS.has(button.id) && isItemVisible('hr', button.id) && (
                    <MenuItem 
                      key={button.id}
                      active={isActive(button.path)} 
                      onClick={() => handleNavigation(button.path)}
                      isCollapsed={isCollapsed}
                      onMouseEnter={(e) => handleSectionMouseEnter('hr', e)}
                      onMouseLeave={handleSectionMouseLeave}
                      sx={{ 
                        paddingLeft: isCollapsed ? 2 : 4,
                        minHeight: isCollapsed ? 'auto' : 56, 
                        alignItems: 'center'
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: 'var(--sidebar-text-color, white)', 
                        minWidth: isSmallScreen ? 16 : 40,
                        marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0,
                        alignSelf: 'flex-start',
                        mt: isCollapsed ? 0 : 0.5
                      }}>
                        {button.icon}
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText 
                          primary={t(`sidebar.hrButtons.${button.id}`)} 
                          primaryTypographyProps={{ 
                            fontSize: isSmallScreen ? '0.65rem' : '0.875rem',
                            whiteSpace: 'normal'
                          }} 
                          sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                        />
                      )}
                    </MenuItem>
                  )
                ))
              ) : (
                <MenuItem isCollapsed={isCollapsed} sx={{ paddingLeft: isCollapsed ? 2 : 4, fontStyle: 'italic', opacity: 0.7 }}>
                  {!isCollapsed && <ListItemText primary={t('sidebar.comingSoonMenu')} />}
                </MenuItem>
              )}
            </Collapse>
          </>
        )}

        {/* Akıllı Depo WMS Başlığı */}
        {visibleSections.wms && (
          <>
            <GroupHeader 
              id="group-header-wms"
              onClick={toggleWms} 
              isCollapsed={isCollapsed}
              onMouseEnter={(e) => handleSectionMouseEnter('wms', e)}
              onMouseLeave={handleSectionMouseLeave}
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: isSmallScreen ? 16 : 40, marginRight: isSmallScreen ? 0.5 : 0 }}>
                  <WarehouseIcon />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.smartWarehouseWms')} 
                    primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold', fontSize: isSmallScreen ? '0.7rem' : '0.875rem' }} 
                  />
                )}
              </Box>
              {!isCollapsed && (wmsOpen ? <ExpandLess /> : <ExpandMore />)}
            </GroupHeader>

            <Collapse in={wmsOpen} timeout="auto" unmountOnExit>
              {WMS_BUTTONS.map((button) => (
                isItemVisible('wms', button.id) && (
                  <MenuItem 
                    key={button.id}
                    active={isActive(button.path)} 
                    onClick={() => handleNavigation(button.path)}
                    isCollapsed={isCollapsed}
                    onMouseEnter={(e) => handleSectionMouseEnter('wms', e)}
                    onMouseLeave={handleSectionMouseLeave}
                    sx={{ 
                      paddingLeft: isCollapsed ? 2 : 4,
                      minHeight: isCollapsed ? 'auto' : 56, 
                      alignItems: 'center'
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: 'var(--sidebar-text-color, white)', 
                      minWidth: isSmallScreen ? 16 : 40,
                      marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0,
                      alignSelf: 'flex-start',
                      mt: isCollapsed ? 0 : 0.5
                    }}>
                      {button.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={t(`sidebar.wmsButtons.${button.id}`)} 
                        primaryTypographyProps={{ 
                          fontSize: isSmallScreen ? '0.65rem' : '0.875rem',
                          whiteSpace: 'normal'
                        }} 
                        sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                      />
                    )}
                  </MenuItem>
                )
              ))}
            </Collapse>
          </>
        )}
        
        {/* Cihaz Yönetimi Başlığı */}
        {visibleSections.device_management && (
          <>
            <GroupHeader 
              id="group-header-device-management"
              onClick={toggleDeviceManagement} 
              isCollapsed={isCollapsed}
              onMouseEnter={(e) => handleSectionMouseEnter('device_management', e)}
              onMouseLeave={handleSectionMouseLeave}
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <DevicesIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.deviceManagement')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      fontWeight: 'bold',
                      noWrap: true
                    }}
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </Box>
              {!isCollapsed && (deviceManagementOpen ? <ExpandLess /> : <ExpandMore />)}
            </GroupHeader>
            
            {/* Cihaz Yönetimi altındaki butonlar */}
            <Collapse in={deviceManagementOpen} timeout="auto" unmountOnExit>
              {DEVICE_MANAGEMENT_BUTTONS.map((button) => (
                isItemVisible('device_management', button.id) && (
                  <MenuItem 
                    key={button.id}
                    active={isActive(button.path)} 
                    onClick={() => handleNavigation(button.path)}
                    isCollapsed={isCollapsed}
                    onMouseEnter={(e) => handleSectionMouseEnter('device_management', e)}
                    onMouseLeave={handleSectionMouseLeave}
                    sx={{ 
                      paddingLeft: isCollapsed ? 2 : 4
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: 'var(--sidebar-text-color, white)', 
                      minWidth: isSmallScreen ? 16 : 40,
                      marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                    }}>
                      {button.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={t(`sidebar.deviceManagementButtons.${button.id}`)} 
                        primaryTypographyProps={{ 
                          fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                          noWrap: true
                        }} 
                        sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                      />
                    )}
                  </MenuItem>
                )
              ))}
            </Collapse>
          </>
        )}
        
        {/* Kart & Cüzdan Yönetimi Başlığı */}
        {visibleSections.card_management && (
          <>
            <GroupHeader 
              id="group-header-card-management"
              onClick={toggleCardManagement} 
              isCollapsed={isCollapsed}
              onMouseEnter={(e) => handleSectionMouseEnter('card_management', e)}
              onMouseLeave={handleSectionMouseLeave}
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <CreditCardIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.cardManagement')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      fontWeight: 'bold',
                      noWrap: true
                    }}
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </Box>
              {!isCollapsed && (cardManagementOpen ? <ExpandLess /> : <ExpandMore />)}
            </GroupHeader>
            
            <Collapse in={cardManagementOpen} timeout="auto" unmountOnExit>
              {CARD_MANAGEMENT_BUTTONS.map((button) => (
                isItemVisible('card_management', button.id) && (
                  <MenuItem 
                    key={button.id}
                    active={isActive(button.path)} 
                    onClick={() => handleNavigation(button.path)}
                    isCollapsed={isCollapsed}
                    onMouseEnter={(e) => handleSectionMouseEnter('card_management', e)}
                    onMouseLeave={handleSectionMouseLeave}
                    sx={{ 
                      paddingLeft: isCollapsed ? 2 : 4
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: 'var(--sidebar-text-color, white)', 
                      minWidth: isSmallScreen ? 16 : 40,
                      marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                    }}>
                      {button.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={t(`sidebar.cardManagementButtons.${button.id}`)} 
                        primaryTypographyProps={{ 
                          fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                          noWrap: true
                        }} 
                        sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                      />
                    )}
                  </MenuItem>
                )
              ))}
            </Collapse>
          </>
        )}
        
        {/* Şubeler Başlığı */}
        {visibleSections.branches && (
          <>
            <GroupHeader 
              id="group-header-branches"
              onClick={toggleBranches} 
              isCollapsed={isCollapsed}
              onMouseEnter={(e) => handleSectionMouseEnter('branches', e)}
              onMouseLeave={handleSectionMouseLeave}
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <BusinessIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.branches')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      fontWeight: 'bold',
                      noWrap: true
                    }}
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </Box>
              {!isCollapsed && (branchesOpen ? <ExpandLess /> : <ExpandMore />)}
            </GroupHeader>
            
            {/* Şubeler altındaki butonlar */}
            <Collapse in={branchesOpen} timeout="auto" unmountOnExit>
              {BRANCHES_BUTTONS.map((button) => (
                isItemVisible('branches', button.id) && (
                  <MenuItem 
                    key={button.id}
                    active={isActive(button.path)} 
                    onClick={() => handleNavigation(button.path)}
                    isCollapsed={isCollapsed}
                    onMouseEnter={(e) => handleSectionMouseEnter('branches', e)}
                    onMouseLeave={handleSectionMouseLeave}
                    sx={{ 
                      paddingLeft: isCollapsed ? 2 : 4
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: 'var(--sidebar-text-color, white)', 
                      minWidth: isSmallScreen ? 16 : 40,
                      marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                    }}>
                      {button.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={t(`sidebar.branchesButtons.${button.id}`)} 
                        primaryTypographyProps={{ 
                          fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                          noWrap: true
                        }} 
                        sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                      />
                    )}
                  </MenuItem>
                )
              ))}
            </Collapse>
          </>
        )}
        
        {/* Diğer Başlığı */}
        {visibleSections.other && (
          <>
            <GroupHeader 
              id="group-header-other"
              onClick={toggleOther} 
              isCollapsed={isCollapsed}
              onMouseEnter={(e) => handleSectionMouseEnter('other', e)}
              onMouseLeave={handleSectionMouseLeave}
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ 
                  color: 'var(--sidebar-text-color, white)', 
                  minWidth: isSmallScreen ? 16 : 40,
                  marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                }}>
                  <EventIcon sx={{ fontSize: isSmallScreen ? 16 : 24 }} />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText 
                    primary={t('sidebar.other')}
                    primaryTypographyProps={{ 
                      fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                      fontWeight: 'bold',
                      noWrap: true
                    }}
                    sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                  />
                )}
              </Box>
              {!isCollapsed && (otherOpen ? <ExpandLess /> : <ExpandMore />)}
            </GroupHeader>
            
            {/* Diğer altındaki butonlar */}
            <Collapse in={otherOpen} timeout="auto" unmountOnExit>
              {OTHER_BUTTONS.map((button) => (
                !TEMP_HIDDEN_OTHER_BUTTON_IDS.has(button.id) &&
                (button.id !== 'users' || userDetails?.role === 'admin') &&
                isItemVisible('other', button.id) && (
                  <MenuItem 
                    key={button.id}
                    active={isActive(button.path)} 
                    onClick={() => handleNavigation(button.path)}
                    isCollapsed={isCollapsed}
                    onMouseEnter={(e) => handleSectionMouseEnter('other', e)}
                    onMouseLeave={handleSectionMouseLeave}
                    sx={{ 
                      paddingLeft: isCollapsed ? 2 : 4
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: 'var(--sidebar-text-color, white)', 
                      minWidth: isSmallScreen ? 16 : 40,
                      marginRight: isSmallScreen && !isCollapsed ? 0.5 : 0
                    }}>
                      {button.icon}
                    </ListItemIcon>
                    {!isCollapsed && (
                      <ListItemText 
                        primary={t(`sidebar.otherButtons.${button.id}`)} 
                        primaryTypographyProps={{ 
                          fontSize: isSmallScreen ? '0.65rem' : 'inherit',
                          noWrap: true
                        }} 
                        sx={{ margin: isSmallScreen ? 0 : 'auto' }}
                      />
                    )}
                  </MenuItem>
                )
              ))}
            </Collapse>
          </>
        )}
      </MenuContainer>
      
      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
      
      <Box sx={{ 
        mt: 'auto', 
        p: isSmallScreen ? 1 : 2, 
        textAlign: 'center',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Link 
          href={BRAND.contact.website}
          target="_blank" 
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          {!isCollapsed ? (
            <>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'var(--sidebar-text-color, white)',
                  opacity: 0.8,
                  mb: 0.5,
                  fontSize: isSmallScreen ? '0.7rem' : 'inherit'
                }}
              >
                {t('sidebar.poweredBy')}
              </Typography>
              <Typography 
                variant={isSmallScreen ? "subtitle1" : "h6"} 
                sx={{ 
                  color: 'var(--sidebar-text-color, white)',
                  fontWeight: 'bold',
                  fontSize: isSmallScreen ? '1rem' : 'inherit',
                  '&:hover': {
                    color: theme.palette.primary.light,
                    transition: 'color 0.2s'
                  }
                }}
              >
                {BRAND.companyName.toUpperCase()}
              </Typography>
              {/* ✅ Sürüm numarası (package.json'dan otomatik) */}
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--sidebar-text-color, white)',
                  opacity: 0.65,
                  display: 'block',
                  mt: 0.25,
                  fontSize: isSmallScreen ? '0.65rem' : '0.7rem',
                  letterSpacing: 0.5,
                }}
              >
                v{APP_VERSION}
              </Typography>
            </>
          ) : (
            <Typography 
              variant={isSmallScreen ? "h6" : "h5"} 
              sx={{ 
                color: 'var(--sidebar-text-color, white)',
                fontWeight: 'bold',
                '&:hover': {
                  color: theme.palette.primary.light,
                  transition: 'color 0.2s'
                }
              }}
            >
              MT
            </Typography>
          )}
        </Link>
      </Box>
      
      {/* SidebarSettingsModal bileşeni */}
      {settingsModalOpen && (
        <SidebarSettingsModal 
          open={settingsModalOpen} 
          onClose={handleSettingsModal} 
        />
      )}

      {/* Formdan ayrılma onayı */}
      <FormLeaveConfirmDialog
        open={leaveConfirmPath !== null}
        onCancel={() => setLeaveConfirmPath(null)}
        onConfirm={() => {
          if (leaveConfirmPath) doNavigate(leaveConfirmPath);
          setLeaveConfirmPath(null);
        }}
      />

      {/* Flyout Menü - Daraltılmış modda hover ile açılır */}
      {isCollapsed && hoveredSection && (
        <FlyoutMenu
          key={hoveredSection} // Her section değiştiğinde DOM'u yeniden oluştur
          ref={flyoutRef}
          onMouseEnter={handleFlyoutMouseEnter}
          onMouseLeave={handleFlyoutMouseLeave}
          sx={{
            top: flyoutPosition.top,
            left: flyoutPosition.left,
            maxHeight: flyoutMaxHeight ? `${flyoutMaxHeight}px` : '80vh',
          }}
        >
          {/* Yönetici Ekranı Flyout */}
          {hoveredSection === 'quick_start' && (
            <>
              <FlyoutHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RocketLaunchIcon sx={{ color: '#4caf50' }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Yönetici Ekranı
                  </Typography>
                </Box>
              </FlyoutHeader>
              {QUICK_START_BUTTONS.filter((button) =>
                // Şirket modül kontrolü: 'reports' görev takibine aittir, diğerleri kendi id'siyle eşleşir.
                button.id === 'reports' ? isModuleEnabled('task_tracking') : isModuleEnabled(button.id)
              ).map((button) => (
                <FlyoutMenuItem
                  key={button.id}
                  onClick={() => handleQuickStartButtonClick(button)}
                  sx={{
                    '&:hover': {
                      backgroundColor: `${button.color}20`,
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: button.color, minWidth: 40 }}>
                    {button.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={button.title || t(button.titleKey || '')} 
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        color: 'var(--sidebar-text-color, white)',
                        fontWeight: 500
                      } 
                    }}
                  />
                </FlyoutMenuItem>
              ))}
            </>
          )}

          {/* Görev Takip Flyout */}
          {hoveredSection === 'task_tracking' && (
            <>
              <FlyoutHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrackChangesIcon />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('sidebar.taskTracking')}
                  </Typography>
                </Box>
              </FlyoutHeader>
              {TASK_TRACKING_BUTTONS.map((button) => (
                isItemVisible('task_tracking', button.id) && (
                  <FlyoutMenuItem
                    key={button.id}
                    active={isActive(button.path)}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(`sidebar.taskTrackingButtons.${button.id}`)} />
                  </FlyoutMenuItem>
                )
              ))}
            </>
          )}

          {/* İnsan Kaynakları Flyout */}
          {hoveredSection === 'hr' && (
            <>
              <FlyoutHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SupervisedUserCircleIcon />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('sidebar.humanResources')}
                  </Typography>
                </Box>
              </FlyoutHeader>
              {HR_BUTTONS.map((button) => (
                !TEMP_HIDDEN_HR_BUTTON_IDS.has(button.id) && isItemVisible('hr', button.id) && (
                  <FlyoutMenuItem
                    key={button.id}
                    active={isActive(button.path)}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(`sidebar.hrButtons.${button.id}`)} />
                  </FlyoutMenuItem>
                )
              ))}
            </>
          )}

          {/* Ön Muhasebe Flyout */}
          {hoveredSection === 'accounting' && (
            <>
              <FlyoutHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceWalletIcon />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('menu.preAccounting', { ns: 'accounting' })}
                  </Typography>
                </Box>
              </FlyoutHeader>
              {ACCOUNTING_BUTTONS.map((button) => (
                isItemVisible('accounting', button.id) && (
                  <FlyoutMenuItem
                    key={button.id}
                    active={isActive(button.path)}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={t(`menu.accountingButtons.${button.id}`, {
                        ns: 'accounting',
                        defaultValue: t(`sidebar.accountingButtons.${button.id}`)
                      })}
                    />
                  </FlyoutMenuItem>
                )
              ))}
              {/* Çek/Senet */}
              {isItemVisible('accounting', 'checksNotes') && (
              <FlyoutMenuItem
                active={isActive('/cek-senet')}
                onClick={() => handleNavigation('/cek-senet')}
              >
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <ReceiptLongIcon />
                </ListItemIcon>
                <ListItemText primary={t('sidebar.checksNotes')} />
              </FlyoutMenuItem>
              )}
              {/* Krediler */}
              {isItemVisible('accounting', 'loans') && (
              <FlyoutMenuItem
                active={isActive('/krediler')}
                onClick={() => handleNavigation('/krediler')}
              >
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <RequestQuoteIcon />
                </ListItemIcon>
                <ListItemText primary={t('sidebar.loans')} />
              </FlyoutMenuItem>
              )}
              {/* Projeler (PYS) */}
              {isItemVisible('accounting', 'projects') && (
              <FlyoutMenuItem
                active={isActive('/projeler')}
                onClick={() => handleNavigation('/projeler')}
              >
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <AccountTreeIcon />
                </ListItemIcon>
                <ListItemText primary={t('sidebar.projects')} />
              </FlyoutMenuItem>
              )}
              {/* Fatura */}
              {isItemVisible('accounting', 'invoice') && (
              <FlyoutMenuItem
                active={isActive('/fatura/alis')}
                onClick={() => handleNavigation('/fatura/alis')}
              >
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <ReceiptIcon />
                </ListItemIcon>
                <ListItemText primary={t('sidebar.invoice')} />
              </FlyoutMenuItem>
              )}
              {/* E-Fatura */}
              {isItemVisible('accounting', 'eInvoice') && (
              <FlyoutMenuItem
                active={isActive('/e-fatura')}
                onClick={() => handleNavigation('/e-fatura')}
              >
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText primary={t('menu.eInvoice', { ns: 'accounting' })} />
              </FlyoutMenuItem>
              )}
              {/* Teklifler */}
              {isItemVisible('accounting', 'offers') && (
              <FlyoutMenuItem
                active={isActive('/teklifler')}
                onClick={() => handleNavigation('/teklifler')}
              >
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <LocalOfferIcon />
                </ListItemIcon>
                <ListItemText primary={t('sidebar.offers')} />
              </FlyoutMenuItem>
              )}
              {/* İrsaliyeler */}
              {isItemVisible('accounting', 'waybills') && (
              <FlyoutMenuItem
                active={isActive('/irsaliyeler')}
                onClick={() => handleNavigation('/irsaliyeler')}
              >
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <LocalShippingIcon />
                </ListItemIcon>
                <ListItemText primary={t('sidebar.waybills')} />
              </FlyoutMenuItem>
              )}
              {/* Siparişler */}
              {isItemVisible('accounting', 'orders') && (
              <FlyoutMenuItem
                active={isActive('/siparisler')}
                onClick={() => handleNavigation('/siparisler')}
              >
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <ShoppingCartIcon />
                </ListItemIcon>
                <ListItemText primary={t('sidebar.orders')} />
              </FlyoutMenuItem>
              )}
            </>
          )}

          {/* Satış Sistemi Flyout */}
          {hoveredSection === 'sales_system' && (
            <>
              <FlyoutHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorefrontIcon />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('sidebar.salesSystem')}
                  </Typography>
                </Box>
              </FlyoutHeader>
              {SALES_SYSTEM_BUTTONS.map((button: any) => {
                if (button.requireHacioglu && !enableHaciogluExport) {
                  return null;
                }
                return isItemVisible('sales_system', button.id) && (
                  <FlyoutMenuItem
                    key={button.id}
                    active={isActive(button.path)}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(`sidebar.salesSystemButtons.${button.id}`)} />
                  </FlyoutMenuItem>
                );
              })}
            </>
          )}

          {/* WMS Flyout */}
          {hoveredSection === 'wms' && (
            <>
              <FlyoutHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarehouseIcon />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('sidebar.smartWarehouseWms')}
                  </Typography>
                </Box>
              </FlyoutHeader>
              {WMS_BUTTONS.map((button) => (
                isItemVisible('wms', button.id) && (
                  <FlyoutMenuItem
                    key={button.id}
                    active={isActive(button.path)}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(`sidebar.wmsButtons.${button.id}`)} />
                  </FlyoutMenuItem>
                )
              ))}
            </>
          )}

          {/* Cihaz Yönetimi Flyout */}
          {hoveredSection === 'device_management' && (
            <>
              <FlyoutHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DevicesIcon />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('sidebar.deviceManagement')}
                  </Typography>
                </Box>
              </FlyoutHeader>
              {DEVICE_MANAGEMENT_BUTTONS.map((button) => (
                isItemVisible('device_management', button.id) && (
                  <FlyoutMenuItem
                    key={button.id}
                    active={isActive(button.path)}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(`sidebar.deviceManagementButtons.${button.id}`)} />
                  </FlyoutMenuItem>
                )
              ))}
            </>
          )}

          {/* Şubeler Flyout */}
          {hoveredSection === 'branches' && (
            <>
              <FlyoutHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('sidebar.branches')}
                  </Typography>
                </Box>
              </FlyoutHeader>
              {BRANCHES_BUTTONS.map((button) => (
                isItemVisible('branches', button.id) && (
                  <FlyoutMenuItem
                    key={button.id}
                    active={isActive(button.path)}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(`sidebar.branchesButtons.${button.id}`)} />
                  </FlyoutMenuItem>
                )
              ))}
            </>
          )}

          {/* Diğer Flyout */}
          {hoveredSection === 'other' && (
            <>
              <FlyoutHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('sidebar.other')}
                  </Typography>
                </Box>
              </FlyoutHeader>
              {OTHER_BUTTONS.map((button) => (
                !TEMP_HIDDEN_OTHER_BUTTON_IDS.has(button.id) &&
                (button.id !== 'users' || userDetails?.role === 'admin') &&
                isItemVisible('other', button.id) && (
                  <FlyoutMenuItem
                    key={button.id}
                    active={isActive(button.path)}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(`sidebar.otherButtons.${button.id}`)} />
                  </FlyoutMenuItem>
                )
              ))}
            </>
          )}
        </FlyoutMenu>
      )}
    </SidebarContainer>
  );
};

export default Sidebar; 